import React, { useEffect, useState } from 'react';
import { Box, Button, Grid, InputLabel, MenuItem, OutlinedInput, Select, Typography } from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import { FormDrawer, FormDrawerGroup } from '../../../../../components/form-drawer';
import { DateField } from '../../../../../components/form-page-parts';
import { RowAction } from '../../../../../components/row-action';
import { ACCOUNT_REPORT_STATUSES } from '../../../../../model/sge/account-report/account-report-enums';
import { IAccountReportAddEditRequest } from '../../../../../model/sge/account-report/request/account-report-add-edit-request';
import { IAccountReportResponse } from '../../../../../model/sge/account-report/response/account-report-response';
import { IAttachmentResponse } from '../../../../../model/sge/attachment/response/attachment-response';
import { formatDate } from '../../../../../utils/date';
import { parseNumberInput } from '../../../../../utils/format';
import { hasErrors } from '../../../../../utils/validation';
import { AccountReportForm, toAccountReportForm, validateAccountReport } from './amendment-detail-form-utils';

interface AccountReportDrawerProps {
    open: boolean;
    initial?: IAccountReportResponse;
    attachments: IAttachmentResponse[];
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: IAccountReportAddEditRequest) => void;
    onDownloadAttachment: (id: number, fileName: string) => void;
    onDeleteAttachment: (id: number) => void;
}

export function AccountReportDrawer({ open, initial, attachments, isSaving, onClose, onSave, onDownloadAttachment, onDeleteAttachment }: AccountReportDrawerProps) {
    const [form, setForm] = useState<AccountReportForm>({});
    const [files, setFiles] = useState<File[]>([]);
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const isEdit = !!initial;
    const validation = validateAccountReport(form);
    const errors = showValidationErrors ? validation : {};

    useEffect(() => {
        if (!open) return;
        setForm(toAccountReportForm(initial));
        setFiles([]);
        setShowValidationErrors(false);
    }, [open, initial]);

    function update(patch: Partial<AccountReportForm>) {
        setForm((current) => ({ ...current, ...patch }));
    }

    function handleSave() {
        if (hasErrors(validation)) {
            setShowValidationErrors(true);
            return;
        }
        onSave({ ...form, files: files.length > 0 ? files : undefined } as IAccountReportAddEditRequest);
    }

    return (
        <FormDrawer
            open={open}
            icon={<FactCheckIcon />}
            title={isEdit ? 'Editar prestação de contas' : 'Nova prestação de contas'}
            subtitle={initial ? `Período de ${formatDate(initial.periodStartDate)} a ${formatDate(initial.periodEndDate)}` : undefined}
            saveLabel={isEdit ? 'Salvar alterações' : 'Adicionar prestação de contas'}
            isSaving={isSaving}
            onClose={onClose}
            onSave={handleSave}
        >
            <FormDrawerGroup title="Período">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <DateField label="Início do período" required value={form.periodStartDate} onChange={(value) => update({ periodStartDate: value || undefined })} error={errors.periodStartDate} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <DateField label="Fim do período" required value={form.periodEndDate} onChange={(value) => update({ periodEndDate: value || undefined })} error={errors.periodEndDate} />
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            {isEdit && (
                <FormDrawerGroup title="Acompanhamento">
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <DateField label="Data de envio" value={form.submissionDate} onChange={(value) => update({ submissionDate: value || undefined })} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <DateField label="Data de revisão" value={form.reviewDate} onChange={(value) => update({ reviewDate: value || undefined })} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <InputLabel>Status</InputLabel>
                            <Select fullWidth value={form.status ?? ''} onChange={(e) => update({ status: parseNumberInput(e.target.value) })}>
                                <MenuItem value=""><em>Selecionar</em></MenuItem>
                                {ACCOUNT_REPORT_STATUSES.map((s) => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
                            </Select>
                        </Grid>
                    </Grid>
                </FormDrawerGroup>
            )}

            <FormDrawerGroup title="Observações">
                <OutlinedInput fullWidth multiline rows={3} value={form.observations || ''} onChange={(e) => update({ observations: e.target.value })} />
            </FormDrawerGroup>

            <FormDrawerGroup title="Anexos" description="Os arquivos selecionados são enviados ao salvar">
                {isEdit && (
                    <Box sx={{ mb: 1.5 }}>
                        {attachments.length === 0 && <Typography variant="body2" color="text.secondary">Nenhum arquivo enviado ainda</Typography>}
                        {attachments.map((a) => (
                            <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                                <Typography variant="body2" noWrap>{a.title || a.originalFileName}</Typography>
                                <Box sx={{ display: 'flex' }}>
                                    <RowAction title="Baixar" onClick={() => onDownloadAttachment(a.id, a.originalFileName || 'arquivo')}>
                                        <DownloadIcon fontSize="small" />
                                    </RowAction>
                                    <RowAction title="Excluir" color="error" onClick={() => onDeleteAttachment(a.id)}>
                                        <DeleteIcon fontSize="small" />
                                    </RowAction>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
                <Button variant="outlined" component="label" startIcon={<AttachFileIcon />}>
                    Selecionar arquivos ({files.length})
                    <input type="file" multiple hidden onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFiles(Array.from(e.target.files || []))} />
                </Button>
                {files.length > 0 && (
                    <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>{files.map((f) => f.name).join(', ')}</Typography>
                )}
            </FormDrawerGroup>
        </FormDrawer>
    );
}
